import React, { createContext, useMemo, useCallback } from 'react';

import defaultConfig from '../config';
import useLocalStorage from '../hooks/useLocalStorage';

import type { ReactNode } from 'react';

// Define the configuration context type
interface ConfigContextType {
  fontFamily: string;
  borderRadius: number;
  outlinedFilled: boolean;
  navType: 'light' | 'dark';
  presetColor: 'default';
  locale: 'en' | 'fr' | 'ro' | 'zh';
  rtlLayout: boolean;
  container: boolean;
  onChangeMenuType: (navType: 'light' | 'dark') => void;
  onChangePresetColor: (presetColor: 'default') => void;
  onChangeLocale: (locale: 'en' | 'fr' | 'ro' | 'zh') => void;
  onChangeRTL: (rtlLayout: boolean) => void;
  onChangeContainer: () => void;
  onChangeFontFamily: (fontFamily: string) => void;
  onChangeBorderRadius: (event: Event, newValue: number) => void;
  onChangeOutlinedField: (outlinedFilled: boolean) => void;
}

// Initial state
const initialState: ConfigContextType = {
  ...defaultConfig,
  onChangeMenuType: () => {},
  onChangePresetColor: () => {},
  onChangeLocale: () => {},
  onChangeRTL: () => {},
  onChangeContainer: () => {},
  onChangeFontFamily: () => {},
  onChangeBorderRadius: () => {},
  onChangeOutlinedField: () => {}
};

// Create context
const ConfigContext = createContext<ConfigContextType>(initialState);

// Props type for the provider
interface ConfigProviderProps {
  children: ReactNode;
}

// ConfigProvider component
const ConfigProvider: React.FC<ConfigProviderProps> = ({ children }) => {
  const [config, setConfig] = useLocalStorage('tma-config', {
    fontFamily: initialState.fontFamily,
    borderRadius: initialState.borderRadius,
    outlinedFilled: initialState.outlinedFilled,
    navType: initialState.navType,
    presetColor: initialState.presetColor,
    locale: initialState.locale,
    rtlLayout: initialState.rtlLayout,
    container: initialState.container
  });

  const onChangeMenuType = useCallback(
    (navType: 'light' | 'dark') => {
      setConfig((prev) => ({ ...prev, navType }));
    },
    [setConfig]
  );

  const onChangePresetColor = useCallback(
    (presetColor: 'default') => {
      setConfig((prev) => ({ ...prev, presetColor }));
    },
    [setConfig]
  );

  const onChangeLocale = useCallback(
    (locale: 'en' | 'fr' | 'ro' | 'zh') => {
      setConfig((prev) => ({ ...prev, locale }));
    },
    [setConfig]
  );

  const onChangeRTL = useCallback(
    (rtlLayout: boolean) => {
      setConfig((prev) => ({ ...prev, rtlLayout }));
    },
    [setConfig]
  );

  const onChangeContainer = useCallback(() => {
    setConfig((prev) => ({ ...prev, container: !prev.container }));
  }, [setConfig]);

  const onChangeFontFamily = useCallback(
    (fontFamily: string) => {
      setConfig((prev) => ({ ...prev, fontFamily }));
    },
    [setConfig]
  );

  const onChangeBorderRadius = useCallback(
    (_event: Event, newValue: number) => {
      setConfig((prev) => ({ ...prev, borderRadius: newValue }));
    },
    [setConfig]
  );

  const onChangeOutlinedField = useCallback(
    (outlinedFilled: boolean) => {
      setConfig((prev) => ({ ...prev, outlinedFilled }));
    },
    [setConfig]
  );

  const contextValue = useMemo(
    () => ({
      ...config,
      onChangeMenuType,
      onChangePresetColor,
      onChangeLocale,
      onChangeRTL,
      onChangeContainer,
      onChangeFontFamily,
      onChangeBorderRadius,
      onChangeOutlinedField
    }),
    [
      config,
      onChangeMenuType,
      onChangePresetColor,
      onChangeLocale,
      onChangeRTL,
      onChangeContainer,
      onChangeFontFamily,
      onChangeBorderRadius,
      onChangeOutlinedField
    ]
  );

  return <ConfigContext.Provider value={contextValue}>{children}</ConfigContext.Provider>;
};

export { ConfigProvider, ConfigContext };
